/**
 * The partner's branded app (S3), on its own domain: its communities only,
 * with a short menu (communities, sign in / out). See partner/site.ts.
 *
 *   /                      the partner's communities
 *   /il|ua|us/:place       one community: its page, and once signed in the
 *                          community itself (pages/dashboard/ScoopDetail)
 *   /signin                white label: this domain's own sign-in (PartnerOwnSignIn);
 *                          otherwise on to soltik.app's
 *   /login, /magic, /oauth/authorize (with or without a locale)
 *                          sign-in is on soltik.app: to the central sign-in,
 *                          or the same page on soltik.app
 *   anything else          back to /
 *
 * Sign-in is soltik.app's (central sign-in, see partner/site.ts): "Sign in"
 * goes to /auth/partner/start, and sign-out to soltik-web's /auth/partner/sign-out.
 * Settings, studio, stores and the rest of soltik stay on soltik.app.
 */
import { Suspense, lazy, useEffect, useState, type ReactNode } from 'react';
import { createBrowserRouter, Link, Navigate, RouterProvider, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, LogIn, LogOut, Users } from 'lucide-react';
import { locales } from '../i18n';
import { clearAllSessions } from '../utils/secureToken';
import { hardNavigate, onColor, signInHref, type PartnerSite, PARTNER_REGIONS } from './site';

const ScoopDetail = lazy(() => import('../pages/dashboard/ScoopDetail'));
const PartnerOwnSignIn = lazy(() => import('./PartnerOwnSignIn'));

const API_URL = import.meta.env.VITE_API_URL || '/api';

export interface PartnerCommunity {
  region: string;
  place: string;
  name: string;
  description: string | null;
  slug: string;
  hub_id: string;
  locale: string;
  members: number;
  emergency: boolean;
}

/** The UI language on a partner page without a locale in its path. */
function partnerLocale(site: PartnerSite): string {
  let saved: string | null = null;
  try { saved = localStorage.getItem('soltik-locale'); } catch { /* storage blocked */ }
  const pick = [saved, site.default_locale, ...(navigator.languages || []).map((l) => l.split('-')[0])]
    .find((l) => l && (locales as readonly string[]).includes(l));
  return pick || 'en';
}

const Spinner = () => (
  <div className="flex items-center justify-center py-24"><Loader2 size={28} className="animate-spin text-white/60" /></div>
);

export function PartnerLayout({ site, children }: { site: PartnerSite; children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const { pathname, search } = useLocation();
  const signedIn = site.signed_in;
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const signInFailed = new URLSearchParams(search).get('signin') === 'failed';
  const brand = site.primary_color || '#e4e63a';

  useEffect(() => {
    if (/^\/(il|ua|us)(\/|$)|^\/$/.test(pathname)) {
      const lng = partnerLocale(site);
      if (i18n.language !== lng) i18n.changeLanguage(lng);
      document.documentElement.lang = lng;
      document.documentElement.dir = ['he', 'ar', 'fa', 'ur'].includes(lng) ? 'rtl' : 'ltr';
    }
  }, [pathname, site, i18n]);

  const signIn = () => hardNavigate(signInHref(site, pathname, i18n.language || 'en'));
  const signOut = async (everywhere: boolean) => {
    setBusy(true);
    try {
      await fetch('/auth/partner/sign-out', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ everywhere }),
      });
    } catch { /* the cookie may already be gone; the page reloads signed out either way */ }
    clearAllSessions();
    hardNavigate('/');
  };

  return (
    <div className="min-h-screen bg-[#0c0c12] text-white flex flex-col">
      <header className="border-b border-white/8" style={{ borderTopWidth: 4, borderTopStyle: 'solid', borderTopColor: brand }}>
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3 min-w-0">
            {site.logo_url && <img src={site.logo_url} alt="" className="w-9 h-9 rounded-lg object-contain bg-white/5" />}
            <span className="font-semibold text-lg truncate">{site.name}</span>
          </Link>
          {signedIn ? (
            <div className="relative">
              <button onClick={() => setSignOutOpen((o) => !o)} aria-expanded={signOutOpen} disabled={busy}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-white/80 hover:text-white hover:bg-white/5 border border-white/10 disabled:opacity-50">
                <LogOut size={15} />
                {t('partnerSite.signOut', 'Sign out')}
              </button>
              {signOutOpen && (
                <div role="menu" className="absolute end-0 mt-2 w-60 rounded-xl bg-[#16161e] border border-white/10 p-1 shadow-xl z-10">
                  <button role="menuitem" onClick={() => signOut(false)} disabled={busy} className="w-full text-start px-3 py-2 rounded-lg text-sm hover:bg-white/5">
                    {t('partnerSite.signOutHere', 'Sign out of {name}', { name: site.name })}
                  </button>
                  <button role="menuitem" onClick={() => signOut(true)} disabled={busy} className="w-full text-start px-3 py-2 rounded-lg text-sm hover:bg-white/5">
                    {t('partnerSite.signOutEverywhere', 'Sign out everywhere')}
                    <span className="block text-xs text-white/50">{t('partnerSite.signOutEverywhereHint', 'Also on soltik.app and your other devices')}</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button onClick={signIn} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium"
              style={{ background: brand, color: onColor(brand) }}>
              <LogIn size={15} />
              {t('partnerSite.signIn', 'Sign in')}
            </button>
          )}
        </div>
      </header>
      <main className="flex-1 w-full max-w-4xl mx-auto px-4 py-6">
        {signInFailed && !signedIn && (
          <p role="alert" className="mb-4 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
            {t('partnerSite.signInFailed', "Sign-in didn't finish. Please try again.")}
          </p>
        )}
        {children}
      </main>
      {site.powered_by_soltik && (
        <footer className="py-6 text-center text-xs text-white/40">
          <a href="https://soltik.app" target="_blank" rel="noopener" className="hover:text-white/70">
            {t('partnerSite.poweredBy', 'Powered by Soltik')}
          </a>
        </footer>
      )}
    </div>
  );
}

function useJson<T>(path: string): { data: T | null; status: number | null } {
  const [state, setState] = useState<{ data: T | null; status: number | null }>({ data: null, status: null });
  useEffect(() => {
    let live = true;
    fetch(`${API_URL}${path}`)
      .then(async (r) => ({ status: r.status, data: r.ok ? await r.json() as T : null }))
      .catch(() => ({ status: 0, data: null }))
      .then((s) => { if (live) setState(s); });
    return () => { live = false; };
  }, [path]);
  return state;
}

function Unavailable() {
  const { t } = useTranslation();
  return <p className="text-center text-white/60 py-24">{t('partnerSite.unavailable', 'This page is not available right now.')}</p>;
}

function EmergencyBadge() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/15 text-red-300 border border-red-500/30">
      <AlertTriangle size={12} />
      {t('partnerSite.emergency', 'Alert in progress')}
    </span>
  );
}

export function PartnerHome() {
  const { t } = useTranslation();
  const { data, status } = useJson<{ communities: PartnerCommunity[] }>('/partner-site');
  if (status === null) return <Spinner />;
  if (!data) return <Unavailable />;
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">{t('partnerSite.communities', 'Communities')}</h1>
      {data.communities.length === 0 ? (
        <p className="text-white/60">{t('partnerSite.noCommunities', 'No communities yet.')}</p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {data.communities.map((c) => (
            <li key={`${c.region}/${c.place}`}>
              <Link to={`/${c.region}/${c.place}`} className="block p-4 rounded-xl bg-white/[0.04] border border-white/8 hover:border-white/20 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium text-lg">{c.name}</span>
                  {c.emergency && <EmergencyBadge />}
                </div>
                <span className="mt-2 flex items-center gap-1.5 text-sm text-white/60">
                  <Users size={14} />
                  {t('partnerSite.members', '{count, plural, one {# member} other {# members}}', { count: c.members })}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function PartnerCommunityPage({ region, site }: { region: string; site: PartnerSite }) {
  const { t, i18n } = useTranslation();
  const { place = '' } = useParams();
  const { pathname } = useLocation();
  const { data, status } = useJson<{ community: PartnerCommunity }>(`/partner-site/communities/${region}/${encodeURIComponent(place)}`);
  const brand = site.primary_color || '#e4e63a';
  if (status === null) return <Spinner />;
  if (!data) return <Unavailable />;
  const c = data.community;
  if (site.signed_in) {
    return (
      <Suspense fallback={<Spinner />}>
        <ScoopDetail hubId={c.hub_id} />
      </Suspense>
    );
  }
  return (
    <div className="space-y-4 py-6">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-3xl font-bold">{c.name}</h1>
        {c.emergency && <EmergencyBadge />}
      </div>
      {c.description && <p className="text-white/70 whitespace-pre-line">{c.description}</p>}
      <p className="flex items-center gap-1.5 text-white/60">
        <Users size={16} />
        {t('partnerSite.members', '{count, plural, one {# member} other {# members}}', { count: c.members })}
      </p>
      <button
        onClick={() => hardNavigate(signInHref(site, pathname, i18n.language || 'en'))}
        className="px-5 py-3 rounded-xl text-base font-semibold"
        style={{ background: brand, color: onColor(brand) }}
      >
        {t('partnerSite.join', 'Sign in to join')}
      </button>
    </div>
  );
}

export function buildPartnerRouter(site: PartnerSite) {
  const page = (el: ReactNode) => <PartnerLayout site={site}><Suspense fallback={<Spinner />}>{el}</Suspense></PartnerLayout>;
  return createBrowserRouter([
    { path: '/', element: page(<PartnerHome />) },
    // White label: sign-in on this domain. Otherwise soltik.app's (central sign-in).
    { path: '/signin', element: site.signin === 'own' ? page(<PartnerOwnSignIn site={site} />) : <ToSoltik site={site} page="login" /> },
    ...PARTNER_REGIONS.map((region) => ({ path: `/${region}/:place`, element: page(<PartnerCommunityPage region={region} site={site} />) })),
    // Sign-in is on soltik.app. Old links and bookmarks: /login → the central sign-in;
    // a magic link or an OIDC authorize request → the same page on soltik.app.
    ...['login', 'magic', 'oauth/authorize'].flatMap((p) => [
      { path: `/${p}`, element: <ToSoltik site={site} page={p} /> },
      { path: `/:locale/${p}`, element: <ToSoltik site={site} page={p} /> },
    ]),
    // A locale-prefixed community path: drop the locale.
    ...PARTNER_REGIONS.map((region) => ({ path: `/:locale/${region}/:place`, element: <StripLocale /> })),
    { path: '*', element: <Navigate to="/" replace /> },
  ]);
}

function StripLocale() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${pathname.replace(/^\/[^/]+/, '')}${search}${hash}`} replace />;
}

/** Sign-in pages live on soltik.app: hand the request over (no form here). */
export function ToSoltik({ site, page }: { site: PartnerSite; page: string }) {
  const { i18n } = useTranslation();
  const { search } = useLocation();
  const { locale } = useParams();
  useEffect(() => {
    const lang = locale && (locales as readonly string[]).includes(locale) ? locale : (i18n.language || 'en');
    hardNavigate(page === 'login' ? signInHref(site, '/', lang) : `${site.soltik_origin}/${lang}/${page}${search}`);
  }, [site, page, search, locale, i18n.language]);
  return <Spinner />;
}

export default function PartnerApp({ site }: { site: PartnerSite }) {
  const [router] = useState(() => buildPartnerRouter(site));
  return <RouterProvider router={router} />;
}
